import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({
    title = "Mega Property Solutions - Auckland's Top Home Insulation & Garage Door Experts",
    description = "Auckland's leading home insulation, garage door installation, and healthy home inspection specialists. Professional services across Auckland with free assessments. Call us for expert property solutions.",
    keywords = "Auckland insulation, garage door installation, home inspection, property maintenance, ceiling insulation, wall insulation, underfloor insulation, healthy home, energy efficiency, Auckland property services, insulation experts, garage door repair, home health assessment",
    canonical = "https://meganz.co.nz",
    ogImage = "/logo-black.png",
    ogType = "website"
}) => {
    // Structured data for local business
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Mega Property Solutions",
        "description": "Auckland's leading home insulation, garage door installation, and healthy home inspection specialists",
        "url": canonical,
        "telephone": "+64-21-520-201",
        "email": "operation@meganz.co.nz",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Auckland",
            "addressRegion": "Auckland",
            "addressCountry": "NZ"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": -36.8485,
            "longitude": 174.7633
        },
        "openingHours": "Mo-Fr 08:00-18:00,Sa 09:00-16:00",
        "priceRange": "$$",
        "image": `${canonical}${ogImage}`,
        "logo": `${canonical}/logo-black.png`,
        "sameAs": [
            "https://www.facebook.com/megapropertysolutions",
            "https://www.instagram.com/megapropertysolutions"
        ],
        "serviceArea": {
            "@type": "GeoCircle",
            "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": -36.8485,
                "longitude": 174.7633
            },
            "geoRadius": "50000"
        },
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Property Services",
            "itemListElement": [
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Home Insulation",
                        "description": "Professional insulation services for ceilings, walls, and underfloor"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Garage Door Installation",
                        "description": "Quality garage door installation, repair, and maintenance services"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Healthy Home Inspection",
                        "description": "Comprehensive home health assessments for family safety"
                    }
                }
            ]
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "127",
            "bestRating": "5",
            "worstRating": "1"
        },
        "review": [
            {
                "@type": "Review",
                "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": "5",
                    "bestRating": "5"
                },
                "author": {
                    "@type": "Person",
                    "name": "Jessica Lee"
                },
                "reviewBody": "Mega Property Solutions transformed our home in Auckland with excellent insulation services. Their team was professional, punctual, and incredibly efficient. We immediately noticed a difference in warmth and energy savings. Highly recommend their services to anyone in need!"
            }
        ]
    };

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content="Mega Property Solutions" />
            <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
            <link rel="canonical" href={canonical} />

            {/* Open Graph Meta Tags */}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={`${canonical}${ogImage}`} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="Mega Property Solutions" />
            <meta property="og:locale" content="en_NZ" />

            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={`${canonical}${ogImage}`} />

            {/* Additional SEO Meta Tags */}
            <meta name="geo.region" content="NZ-AUK" />
            <meta name="geo.placename" content="Auckland, New Zealand" />
            <meta name="geo.position" content="-36.8485;174.7633" />
            <meta name="ICBM" content="-36.8485, 174.7633" />
            <meta name="distribution" content="New Zealand" />
            <meta name="coverage" content="Auckland Region" />
            <meta name="target" content="all" />
            <meta name="rating" content="general" />
            <meta name="revisit-after" content="7 days" />

            {/* Business Schema.org Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>

            {/* Additional Structured Data for Services */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": "Property Services in Auckland",
                    "description": "Professional property services including insulation, garage doors, and home inspections",
                    "url": `${canonical}/services`,
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "item": {
                                "@type": "Service",
                                "name": "Home Insulation Auckland",
                                "description": "Professional insulation services for ceilings, walls, and underfloor in Auckland",
                                "url": `${canonical}/services/insulation`,
                                "areaServed": "Auckland, New Zealand",
                                "serviceType": "Home Insulation"
                            }
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "item": {
                                "@type": "Service",
                                "name": "Garage Door Installation Auckland",
                                "description": "Quality garage door installation, repair, and maintenance in Auckland",
                                "url": `${canonical}/services/garage-doors`,
                                "areaServed": "Auckland, New Zealand",
                                "serviceType": "Garage Door Services"
                            }
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "item": {
                                "@type": "Service",
                                "name": "Healthy Home Inspection Auckland",
                                "description": "Comprehensive home health assessments in Auckland",
                                "url": `${canonical}/services/home-inspection`,
                                "areaServed": "Auckland, New Zealand",
                                "serviceType": "Home Inspection"
                            }
                        }
                    ]
                })}
            </script>

            {/* FAQ Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [
                        {
                            "@type": "Question",
                            "name": "What insulation services do you offer?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "We offer comprehensive insulation services including ceiling insulation, wall insulation, underfloor insulation, and roof space insulation. Our services cover both residential and commercial properties, with options for retrofitting existing buildings and installing insulation in new constructions."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Do you offer free insulation assessments?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Yes, we provide complimentary insulation assessments for all Auckland properties. Our qualified technicians will visit your home to evaluate your current insulation, identify areas of heat loss, and provide a detailed report with recommendations."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Are your services available throughout Auckland?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Absolutely! We service the entire Auckland region including North Shore, Waitakere, Manukau, and the Auckland City area. Our team covers all suburbs from Albany in the north to Pukekohe in the south."
                            }
                        }
                    ]
                })}
            </script>
        </Helmet>
    );
};

export default SEO;
